import { toHumanReadable, useBlockAllKeyboardShortcuts, useEffectOnce } from '@core/logic';
import { useAppDispatch } from '@core/redux/hooks';
import { useIsTelekomProject } from '@core/logic/hooks/useIsTelekomProject';
import { useGetPointElementOrderNumbersQuery } from '@core/redux/query';
import { Point, XOutline } from '@deepup/icons';
import { DIALOG_WIDTH, ElementDialogProps } from '@elements/components';
import { pointElementThunks } from '@elements/redux/point-element.slice';
import type { PointElement } from '@elements/types';
import { Dialog, DialogTitle, IconButton } from '@mui/material';
import { setEditMode } from '@projects/edit-modes';
import { EditMode } from '@projects/edit-modes/types';
import { OrderNumberType } from '@projects/types/orderNumbers';
import { useEffect, useMemo, useState } from 'react';
import { ElementSMAs } from './ElementSMAs';
import { PointElementProperties, useConnect } from './PointElementProperties';

export const PointElementDialog = ({
  elementToUpdate,
  onClose,
  onDelete,
}: ElementDialogProps<PointElement>) => {
  const dispatch = useAppDispatch();
  const { isTelekomProject } = useIsTelekomProject();
  const [editStep, setEditStep] = useState<'PROPERTIES' | 'SMAS'>('PROPERTIES');
  const [editedPointElement, setEditedPointElement] = useState<PointElement>();
  const { data: pointElementOrderNumbers, refetch: refetchPointElementOrderNumbers } =
    useGetPointElementOrderNumbersQuery(editedPointElement?.id);
  const [inheritOrderNumbers, setInheritOrderNumbers] = useState<Set<string>>(new Set());

  const selectedSmas = useMemo(() => {
    return pointElementOrderNumbers?.map((on) => on.value) ?? [];
  }, [pointElementOrderNumbers]);

  useBlockAllKeyboardShortcuts();
  useEffectOnce(() => {
    setEditMode(EditMode.Element);
  });

  useEffect(() => {
    const inheritIds =
      pointElementOrderNumbers?.filter((o) => o.allowInheritance).map((o) => o.value) ?? [];
    setInheritOrderNumbers(new Set(inheritIds));
  }, [pointElementOrderNumbers]);

  const onNext = async (editedPointElement: PointElement) => {
    if (isTelekomProject) {
      setEditStep('SMAS');
      setEditedPointElement(editedPointElement);
    } else {
      onClose();
    }
  };

  const { element } = useConnect({ elementToUpdate });

  const onSubmitSmas = async (smas: string[]) => {
    if (!editedPointElement) {
      onClose();
      return;
    }

    const elementWithSmas: PointElement = {
      ...editedPointElement,
      orderNumbers: smas.map((sma) => ({
        value: sma,
        type: OrderNumberType.SMA,
        allowInheritance: inheritOrderNumbers.has(sma),
      })),
    };

    await dispatch(
      pointElementThunks.patch({
        path: ['projects', editedPointElement.projectId, 'pointElements', editedPointElement.id],
        body: elementWithSmas,
      }),
    );

    refetchPointElementOrderNumbers();

    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        elevation: 4,
        sx: editStep === 'PROPERTIES' ? { width: DIALOG_WIDTH } : {},
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle variant="h6" gap={2} display="flex" alignItems="center">
        <Point fill="currentColor" fontSize={24} />
        {(elementToUpdate ? 'Edit ' : 'Add ') + toHumanReadable(element.kind)}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
          })}
          size="large"
        >
          <XOutline fontSize={24} />
        </IconButton>
      </DialogTitle>
      {editStep === 'PROPERTIES' && (
        <PointElementProperties
          onClose={onClose}
          onDelete={onDelete}
          elementToUpdate={elementToUpdate}
          onNext={onNext}
        />
      )}
      {editStep === 'SMAS' && (
        <ElementSMAs
          selectedSmas={selectedSmas}
          onSubmitSmas={onSubmitSmas}
          onClose={onClose}
          onDelete={onDelete}
          submitButtonLabel="Update Network Node"
          inheritOrderNumbers={inheritOrderNumbers}
          setInheritOrderNumbers={setInheritOrderNumbers}
        />
      )}
    </Dialog>
  );
};
